import { useState, useRef, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Brain, Mic, MessageSquare, ArrowLeft, Send, Lightbulb,
  BookOpen, ChevronRight, AlertCircle, Clock, RefreshCw, Star,
} from '../../components/icons';
import { LoadingSpinner } from '../../components/LoadingSpinner';
import { MarkdownRenderer } from '../../components/ai/MarkdownRenderer';
import { ReframingPracticeVoice } from '../../components/ai/ReframingPracticeVoice';
import {
  sendReframingChatMessage,
  completeReframingSession,
  createReframingSession,
  fetchReframingSessions,
  fetchReframingProgress,
  type ReframingSession,
} from '../../lib/reframing-practice';
import {
  REFRAMING_PRACTICE_MODES,
  REFRAMING_DIFFICULTIES,
  REFRAMING_CONTEXTS,
  REFRAMING_FEEDBACK_TIMINGS,
  REFRAMING_SESSION_LENGTHS,
  REFRAMING_SIMULATION_DURATIONS,
  type ReframingPracticeMode,
  type ReframingDifficulty,
  type ReframingContext,
  type ReframingFeedbackTiming,
  type ReframingSessionLength,
  type ReframingInteractionMode,
} from '../../components/ai/reframingPracticeConstants';

type Step = 'entry' | 'setup' | 'practice_chat' | 'practice_voice' | 'report' | 'history';

interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
}

interface SessionReport {
  durationSeconds: number;
  practiceMode: ReframingPracticeMode;
  interactionMode: ReframingInteractionMode;
  sessionId: string | null;
}

export function ReframingPracticePage() {
  const [step, setStep] = useState<Step>('entry');
  const [practiceMode, setPracticeMode] = useState<ReframingPracticeMode>('identify');
  const [difficulty, setDifficulty] = useState<ReframingDifficulty>('beginner');
  const [context, setContext] = useState<ReframingContext>('coaching');
  const [feedbackTiming, setFeedbackTiming] = useState<ReframingFeedbackTiming>('after_every');
  const [sessionLength, setSessionLength] = useState<ReframingSessionLength>(10);
  const [interactionMode, setInteractionMode] = useState<ReframingInteractionMode>('chat');

  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [chatInput, setChatInput] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [sessionStartTime, setSessionStartTime] = useState<number>(0);
  const [voiceOpen, setVoiceOpen] = useState(false);
  const [report, setReport] = useState<SessionReport | null>(null);
  const [sessions, setSessions] = useState<ReframingSession[]>([]);
  const [progress, setProgress] = useState<Record<string, unknown> | null>(null);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [error, setError] = useState('');

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const selectedMode = REFRAMING_PRACTICE_MODES.find(m => m.id === practiceMode);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  async function loadHistory() {
    setLoadingHistory(true);
    try {
      const [sess, prog] = await Promise.all([fetchReframingSessions(), fetchReframingProgress()]);
      setSessions(sess);
      setProgress(prog);
    } catch { /* ignore */ }
    setLoadingHistory(false);
  }

  async function startChatSession() {
    setInteractionMode('chat');
    setError('');
    setMessages([]);
    setIsSending(true);

    try {
      const id = await createReframingSession({
        practice_mode: practiceMode,
        difficulty,
        context,
        feedback_timing: feedbackTiming,
        interaction_mode: 'chat',
        session_length_questions: selectedMode?.useQuestions ? sessionLength : undefined,
      });
      setSessionId(id);
      setSessionStartTime(Date.now());

      const { reply } = await sendReframingChatMessage({
        message: 'Please start our session. Introduce yourself briefly and present the first exercise.',
        conversation_history: [],
        practice_mode: practiceMode,
        difficulty,
        context,
        feedback_timing: feedbackTiming,
        session_length_questions: selectedMode?.useQuestions ? sessionLength : undefined,
        practice_session_id: id,
      });

      setMessages([{ role: 'assistant', content: reply }]);
      setStep('practice_chat');
    } catch (err) {
      setError((err as Error).message);
    }

    setIsSending(false);
  }

  async function startVoiceSession() {
    setInteractionMode('voice');
    setStep('practice_voice');
    setVoiceOpen(true);
  }

  async function sendMessage() {
    const msg = chatInput.trim();
    if (!msg || isSending) return;
    setChatInput('');
    setIsSending(true);
    setError('');

    const newMessages = [...messages, { role: 'user' as const, content: msg }];
    setMessages(newMessages);

    try {
      const { reply } = await sendReframingChatMessage({
        message: msg,
        conversation_history: messages,
        practice_mode: practiceMode,
        difficulty,
        context,
        feedback_timing: feedbackTiming,
        session_length_questions: selectedMode?.useQuestions ? sessionLength : undefined,
        practice_session_id: sessionId,
      });
      setMessages([...newMessages, { role: 'assistant', content: reply }]);
    } catch (err) {
      setError((err as Error).message);
    }

    setIsSending(false);
  }

  async function sendQuickCommand(command: string) {
    const commands: Record<string, string> = {
      hint: 'Please give me a hint for this exercise without giving away the full answer.',
      feedback: 'Please give me detailed feedback on my last response using the scoring criteria.',
      principle: 'Please remind me of the relevant reframing principle for this exercise.',
      next: 'Please move to the next exercise.',
    };
    if (!commands[command]) return;
    setChatInput(commands[command]);
  }

  async function endChatSession() {
    const elapsed = Math.floor((Date.now() - sessionStartTime) / 1000);
    if (sessionId) {
      await completeReframingSession(sessionId, {
        duration_seconds: elapsed,
        exercises_completed: messages.filter(m => m.role === 'user').length,
      }).catch(() => {});
    }
    setReport({ durationSeconds: elapsed, practiceMode, interactionMode: 'chat', sessionId });
    setStep('report');
  }

  const handleVoiceEnd = useCallback(async (durationSeconds: number, voiceSessionId: string | null) => {
    setVoiceOpen(false);
    if (voiceSessionId) {
      await completeReframingSession(voiceSessionId, { duration_seconds: durationSeconds }).catch(() => {});
    }
    setReport({ durationSeconds, practiceMode, interactionMode: 'voice', sessionId: voiceSessionId });
    setStep('report');
  }, [practiceMode]);

  function formatDuration(s: number) {
    const m = Math.floor(s / 60);
    const sec = s % 60;
    return m > 0 ? `${m}m ${sec}s` : `${sec}s`;
  }

  function formatDate(iso: string) {
    return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  }

  // ─── Entry screen ─────────────────────────────────────────
  if (step === 'entry') {
    return (
      <div className="max-w-3xl mx-auto px-4 py-8">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
            <Brain className="w-7 h-7 text-teal-600" />
            Reframing AI Coach
          </h1>
          <p className="text-gray-500 mt-1">Practise identifying and creating context reframes, meaning reframes and alternative behaviours while receiving personalised AI feedback.</p>
        </div>

        <div className="grid sm:grid-cols-2 gap-4 mb-8">
          <button onClick={() => { setInteractionMode('chat'); setStep('setup'); }}
            className="group p-6 rounded-2xl border-2 border-gray-200 hover:border-teal-500 hover:bg-teal-50 text-left transition-all">
            <MessageSquare className="w-8 h-8 text-teal-600 mb-3" />
            <p className="font-semibold text-gray-900 text-lg">Chat Practice</p>
            <p className="text-gray-500 text-sm mt-1">Type your responses and receive structured written feedback with full scoring breakdowns.</p>
          </button>
          <button onClick={() => { setInteractionMode('voice'); setStep('setup'); }}
            className="group p-6 rounded-2xl border-2 border-gray-200 hover:border-teal-500 hover:bg-teal-50 text-left transition-all">
            <Mic className="w-8 h-8 text-teal-600 mb-3" />
            <p className="font-semibold text-gray-900 text-lg">Voice Practice</p>
            <p className="text-gray-500 text-sm mt-1">Speak naturally with the AI coach. Your speech and the coach's responses appear as a live transcript.</p>
          </button>
        </div>

        <button onClick={() => { loadHistory(); setStep('history'); }}
          className="text-sm text-teal-600 hover:text-teal-700 font-medium flex items-center gap-1 transition-colors">
          <Clock className="w-4 h-4" />
          View session history
        </button>
      </div>
    );
  }

  // ─── Setup modal ───────────────────────────────────────────
  if (step === 'setup') {
    const usesQuestions = selectedMode?.useQuestions ?? true;
    const isSimulation = practiceMode === 'coaching_simulation';

    return (
      <div className="max-w-2xl mx-auto px-4 py-8">
        <button onClick={() => setStep('entry')} className="flex items-center gap-2 text-gray-500 hover:text-gray-700 text-sm mb-6 transition-colors">
          <ArrowLeft className="w-4 h-4" /> Back
        </button>

        <div className="mb-6">
          <h2 className="text-xl font-bold text-gray-900">Reframing AI Coach</h2>
          <p className="text-gray-500 text-sm mt-1">Practise changing context or meaning while preserving facts and expanding choice.</p>
          <div className="mt-2 inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-teal-50 text-teal-700 text-xs font-medium">
            {interactionMode === 'voice' ? <Mic className="w-3 h-3" /> : <MessageSquare className="w-3 h-3" />}
            {interactionMode === 'voice' ? 'Voice Practice' : 'Chat Practice'}
          </div>
        </div>

        <div className="space-y-6">
          {/* Practice type */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Practice type</label>
            <div className="space-y-2">
              {REFRAMING_PRACTICE_MODES.map(m => (
                <button key={m.id} onClick={() => setPracticeMode(m.id)}
                  className={`w-full text-left px-4 py-3 rounded-xl border transition-colors ${practiceMode === m.id ? 'border-teal-500 bg-teal-50' : 'border-gray-200 hover:border-gray-300'}`}>
                  <p className={`font-medium text-sm ${practiceMode === m.id ? 'text-teal-800' : 'text-gray-800'}`}>{m.label}</p>
                  <p className="text-xs text-gray-500 mt-0.5">{m.description}</p>
                </button>
              ))}
            </div>
          </div>

          {/* Difficulty */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Difficulty</label>
            <div className="grid grid-cols-3 gap-2">
              {REFRAMING_DIFFICULTIES.map(d => (
                <button key={d.id} onClick={() => setDifficulty(d.id)}
                  className={`px-3 py-2.5 rounded-xl border text-sm font-medium transition-colors ${difficulty === d.id ? 'border-teal-500 bg-teal-50 text-teal-800' : 'border-gray-200 text-gray-700 hover:border-gray-300'}`}>
                  {d.label}
                </button>
              ))}
            </div>
          </div>

          {/* Context */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Context</label>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {REFRAMING_CONTEXTS.map(c => (
                <button key={c.id} onClick={() => setContext(c.id)}
                  className={`px-3 py-2.5 rounded-xl border text-sm font-medium transition-colors ${context === c.id ? 'border-teal-500 bg-teal-50 text-teal-800' : 'border-gray-200 text-gray-700 hover:border-gray-300'}`}>
                  {c.label}
                </button>
              ))}
            </div>
          </div>

          {/* Feedback timing */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Feedback timing</label>
            <div className="grid grid-cols-2 gap-2">
              {REFRAMING_FEEDBACK_TIMINGS.map(f => (
                <button key={f.id} onClick={() => setFeedbackTiming(f.id)}
                  className={`px-3 py-2.5 rounded-xl border text-sm font-medium transition-colors ${feedbackTiming === f.id ? 'border-teal-500 bg-teal-50 text-teal-800' : 'border-gray-200 text-gray-700 hover:border-gray-300'}`}>
                  {f.label}
                </button>
              ))}
            </div>
          </div>

          {/* Session length */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              {isSimulation ? 'Session length' : 'Number of exercises'}
            </label>
            <div className="grid grid-cols-3 gap-2">
              {(isSimulation ? REFRAMING_SIMULATION_DURATIONS : REFRAMING_SESSION_LENGTHS).map(l => (
                <button key={l.id} onClick={() => setSessionLength(l.id)}
                  className={`px-3 py-2.5 rounded-xl border text-sm font-medium transition-colors ${sessionLength === l.id ? 'border-teal-500 bg-teal-50 text-teal-800' : 'border-gray-200 text-gray-700 hover:border-gray-300'}`}>
                  {l.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {error && (
          <div className="mt-4 flex items-start gap-2 text-red-600 text-sm bg-red-50 rounded-xl p-3">
            <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
            <p>{error}</p>
          </div>
        )}

        <div className="mt-8">
          <button
            onClick={interactionMode === 'voice' ? startVoiceSession : startChatSession}
            disabled={isSending}
            className="w-full py-3.5 bg-teal-600 hover:bg-teal-700 disabled:opacity-50 text-white rounded-xl font-semibold flex items-center justify-center gap-2 transition-colors">
            {isSending ? (
              <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> Starting…</>
            ) : (
              <>{interactionMode === 'voice' ? <Mic className="w-5 h-5" /> : <MessageSquare className="w-5 h-5" />} Start Practice</>
            )}
          </button>
        </div>
      </div>
    );
  }

  // ─── Chat practice ─────────────────────────────────────────
  if (step === 'practice_chat') {
    return (
      <div className="flex flex-col h-[calc(100vh-120px)] max-w-3xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 bg-white">
          <div className="flex items-center gap-2">
            <Brain className="w-5 h-5 text-teal-600" />
            <div>
              <p className="font-semibold text-gray-900 text-sm">Reframing AI Coach</p>
              <p className="text-xs text-gray-500 capitalize">Chat Practice · {practiceMode.replace(/_/g, ' ')} · {difficulty} · {context}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => { setStep('practice_voice'); setVoiceOpen(true); }}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-teal-200 text-teal-700 text-xs font-medium hover:bg-teal-50 transition-colors"
            >
              <Mic className="w-3.5 h-3.5" />
              Switch to Voice
            </button>
            <button onClick={endChatSession}
              className="px-3 py-1.5 rounded-lg border border-gray-200 text-gray-600 text-xs font-medium hover:bg-gray-50 transition-colors">
              End Session
            </button>
          </div>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4">
          <AnimatePresence initial={false}>
            {messages.map((msg, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
                className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[85%] rounded-2xl px-4 py-3 text-sm ${
                  msg.role === 'user'
                    ? 'bg-teal-600 text-white rounded-br-sm'
                    : 'bg-gray-50 text-gray-800 rounded-bl-sm border border-gray-100'
                }`}>
                  {msg.role === 'assistant' ? (
                    <MarkdownRenderer content={msg.content} />
                  ) : (
                    <p>{msg.content}</p>
                  )}
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
          {isSending && (
            <div className="flex justify-start">
              <div className="bg-gray-50 border border-gray-100 rounded-2xl rounded-bl-sm px-4 py-3">
                <div className="flex gap-1">
                  {[0, 1, 2].map(i => (
                    <div key={i} className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: `${i * 0.15}s` }} />
                  ))}
                </div>
              </div>
            </div>
          )}
          {error && (
            <div className="flex items-start gap-2 text-red-600 text-xs bg-red-50 rounded-xl p-3">
              <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <p>{error}</p>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Quick commands */}
        <div className="px-4 pb-2 flex gap-2 overflow-x-auto">
          {[
            { id: 'hint', label: 'Ask for a hint', icon: Lightbulb },
            { id: 'principle', label: 'Show principle', icon: BookOpen },
            { id: 'feedback', label: 'Request feedback', icon: Star },
            { id: 'next', label: 'Next exercise', icon: ChevronRight },
          ].map(({ id, label, icon: Icon }) => (
            <button key={id} onClick={() => sendQuickCommand(id)}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-gray-50 border border-gray-200 text-gray-600 text-xs font-medium whitespace-nowrap hover:bg-gray-100 transition-colors flex-shrink-0">
              <Icon className="w-3.5 h-3.5" />
              {label}
            </button>
          ))}
        </div>

        {/* Input */}
        <div className="px-4 pb-4 pt-1">
          <div className="flex gap-2 items-end">
            <textarea
              ref={inputRef}
              value={chatInput}
              onChange={e => setChatInput(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); } }}
              placeholder="Type your response…"
              rows={2}
              className="flex-1 resize-none rounded-xl border border-gray-200 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
            />
            <button
              onClick={sendMessage}
              disabled={!chatInput.trim() || isSending}
              className="p-3 bg-teal-600 hover:bg-teal-700 disabled:opacity-40 text-white rounded-xl transition-colors">
              <Send className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ─── Voice practice ────────────────────────────────────────
  if (step === 'practice_voice') {
    return (
      <div className="max-w-xl mx-auto px-4 py-8 text-center">
        <Brain className="w-12 h-12 text-teal-600 mx-auto mb-4" />
        <h2 className="text-xl font-bold text-gray-900 mb-2">Voice Session Ready</h2>
        <p className="text-gray-500 text-sm mb-6 capitalize">
          {practiceMode.replace(/_/g, ' ')} · {difficulty} · {context}
        </p>
        <button onClick={() => setVoiceOpen(true)}
          className="px-8 py-3 bg-teal-600 hover:bg-teal-700 text-white rounded-xl font-semibold flex items-center justify-center gap-2 mx-auto transition-colors">
          <Mic className="w-5 h-5" />
          Start Voice Session
        </button>
        <button onClick={() => setStep('setup')} className="mt-4 text-sm text-gray-500 hover:text-gray-700 block mx-auto transition-colors">
          Back to setup
        </button>

        <AnimatePresence>
          {voiceOpen && (
            <ReframingPracticeVoice
              isOpen={voiceOpen}
              onClose={() => { setVoiceOpen(false); setStep('entry'); }}
              practice_mode={practiceMode}
              difficulty={difficulty}
              context={context}
              feedback_timing={feedbackTiming}
              session_length_minutes={selectedMode?.useQuestions ? undefined : sessionLength}
              onSessionEnd={handleVoiceEnd}
            />
          )}
        </AnimatePresence>
      </div>
    );
  }

  // ─── Session report ────────────────────────────────────────
  if (step === 'report' && report) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-8">
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-teal-50 rounded-full flex items-center justify-center mx-auto mb-4">
            <Brain className="w-8 h-8 text-teal-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900">Session Complete</h2>
          <p className="text-gray-500 mt-1">Your Reframing AI Coach session has ended.</p>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-8">
          {[
            { label: 'Practice Mode', value: report.practiceMode.replace(/_/g, ' ') },
            { label: 'Interaction', value: report.interactionMode === 'voice' ? 'Voice' : 'Chat' },
            { label: 'Duration', value: formatDuration(report.durationSeconds) },
          ].map(({ label, value }) => (
            <div key={label} className="bg-gray-50 rounded-xl p-4 text-center">
              <p className="text-xs text-gray-500 mb-1">{label}</p>
              <p className="font-semibold text-gray-900 capitalize">{value}</p>
            </div>
          ))}
        </div>

        <div className="bg-teal-50 rounded-2xl p-6 mb-6">
          <h3 className="font-semibold text-teal-900 mb-2">Your session has been saved.</h3>
          <p className="text-teal-700 text-sm">Your practice history and progress are updated after each session. Review session history to track your improvement over time.</p>
        </div>

        <div className="flex flex-col sm:flex-row gap-3">
          <button onClick={() => { setMessages([]); setSessionId(null); setStep('setup'); }}
            className="flex-1 py-3 bg-teal-600 hover:bg-teal-700 text-white rounded-xl font-semibold transition-colors flex items-center justify-center gap-2">
            <RefreshCw className="w-4 h-4" />
            Practice Again
          </button>
          <button onClick={() => { loadHistory(); setStep('history'); }}
            className="flex-1 py-3 border border-gray-200 hover:bg-gray-50 text-gray-700 rounded-xl font-semibold transition-colors flex items-center justify-center gap-2">
            <Clock className="w-4 h-4" />
            Session History
          </button>
        </div>
        <button onClick={() => setStep('entry')} className="mt-3 w-full text-sm text-gray-500 hover:text-gray-700 transition-colors">
          Back to home
        </button>
      </div>
    );
  }

  // ─── History ────────────────────────────────────────────────
  if (step === 'history') {
    return (
      <div className="max-w-3xl mx-auto px-4 py-8">
        <button onClick={() => setStep('entry')} className="flex items-center gap-2 text-gray-500 hover:text-gray-700 text-sm mb-6 transition-colors">
          <ArrowLeft className="w-4 h-4" /> Back
        </button>
        <h2 className="text-xl font-bold text-gray-900 mb-6">Session History</h2>

        {loadingHistory ? (
          <div className="flex justify-center py-12"><LoadingSpinner /></div>
        ) : sessions.length === 0 ? (
          <div className="text-center py-12 text-gray-400">
            <Clock className="w-10 h-10 mx-auto mb-3 opacity-50" />
            <p>No completed sessions yet.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {sessions.map(s => (
              <div key={s.id} className="border border-gray-100 rounded-xl p-4 hover:bg-gray-50 transition-colors">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="font-medium text-gray-900 capitalize text-sm">
                      {s.practice_mode.replace(/_/g, ' ')}
                    </p>
                    <p className="text-xs text-gray-500 mt-0.5 capitalize">
                      {s.difficulty} · {s.context.replace(/_/g, ' ')} · {s.interaction_mode}
                    </p>
                  </div>
                  <div className="text-right">
                    {s.overall_score !== null && (
                      <p className="text-sm font-bold text-teal-600">{s.overall_score?.toFixed(1)}/12</p>
                    )}
                    <p className="text-xs text-gray-400">{formatDate(s.created_at)}</p>
                  </div>
                </div>
                {s.recommended_next && (
                  <p className="text-xs text-gray-500 mt-2 border-t border-gray-100 pt-2">
                    Recommended next: {s.recommended_next}
                  </p>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  return null;
}
